import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ATTRACTIONS } from "./data";
import SectionHeader from "./SectionHeader";

export default function Attractions() {
  const [_, setActive] = useState(null);

  return (
    <section
      id="places"
      data-testid="attractions-section"
      className="relative bg-[color:var(--paper)] text-[color:var(--ink)]"
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Section 06 · Nearest Places & Attractions"
          title={
            <>
              Eight places, all within a <span className="font-display-italic text-[color:var(--clay)]">short</span> drive.
            </>
          }
          intro="Trekking peaks, waterfalls, forts and a railway — every one of them a small day out."
        />

        <div className="mt-10 sm:mt-14 relative border-t hairline">
          {ATTRACTIONS.map((a, i) => (
            <motion.a
              key={a.name}
              href="#book"
              data-testid={`attraction-${i}`}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.03, duration: 0.6 }}
              className="attraction-row group relative flex items-center gap-3 sm:gap-5 border-b hairline py-4 sm:py-6 px-2 sm:px-4"
            >
              {/* Mobile-only thumbnail */}
              <div className="lg:hidden flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 rounded-2xl overflow-hidden ring-1 ring-[color:var(--line)] bg-[color:var(--forest)]">
                <img
                  src={a.img}
                  alt={a.name}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="flex-1 flex items-baseline gap-3 sm:gap-8 min-w-0">
                <span className="font-mono-alt text-[color:var(--ink)]/50 group-hover:text-[color:var(--paper)]/70 w-7 sm:w-8 flex-shrink-0">
                  {a.n}
                </span>
                <span className="attraction-name font-display text-[clamp(1rem,2.4vw,2rem)] leading-tight tracking-tight truncate">
                  {a.name}
                </span>
              </div>

              <div className="flex items-center gap-2 sm:gap-6 flex-shrink-0">
                <span className="hidden md:inline font-mono-alt text-[color:var(--ink)]/60 group-hover:text-[color:var(--paper)]/70">
                  {a.distance}
                </span>
                <span className="font-mono-alt text-[color:var(--ink)]/45 group-hover:text-[color:var(--paper)]/60 text-[0.65rem] sm:text-[0.72rem]">
                  {a.time}
                </span>
                <ArrowUpRight
                  size={16}
                  className="text-[color:var(--ink)]/50 group-hover:text-[color:var(--paper)] transition-transform group-hover:rotate-45"
                />
              </div>

              {/* Desktop hover thumbnail */}
              <div
                aria-hidden
                className="attraction-thumb pointer-events-none absolute left-1/2 top-1/2 hidden lg:block w-[240px] h-[300px] rounded-[18px] overflow-hidden shadow-2xl ring-1 ring-[color:var(--line-inv)]"
                style={{ zIndex: 5 }}
              >
                <img
                  src={a.img}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.a>
          ))}

          <div className="mt-4 sm:mt-5 flex sm:hidden font-mono-alt text-[color:var(--ink)]/50 justify-center">
            Distances shown are from the homestay
          </div>
        </div>
      </div>
    </section>
  );
}
